import React, { FunctionComponent, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useI18n } from "../../../hooks/useI18n";
import { AppState } from "../../../store";
import { thunkFetchCustomerFeedbackStatistics } from "../../../store/statistics/customerfeedback/actions";
import { CustomerFeedbacksStatisticsState } from "../../../store/statistics/customerfeedback/types";
import StatTable from "../components/StatTable";
import DateRangePicker from "../../../components/inputs/daterangepicker";
import LineChart from "../components/LineChart";
import TableWrapper from "../components/TableWrapper";
import {
  Grid,
  Hidden,
  useMediaQuery,
  Switch,
  Dialog,
  DialogContent,
  IconButton
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import moment from "moment";
import CustomerFeedbackDetailsTable from "./CustomerFeedbackDetailsTable";
import { UserOverview } from "../../users/UserOverview";
import useStyles from "./CustomerFeedbackDetailsTable/styles";
import { Rating } from "@material-ui/lab";
import { Message } from "@material-ui/icons";
import { Tooltip } from "@material-ui/core";
import { LineChartData } from "../components/LineChart/types";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import CloseIcon from "@material-ui/icons/Close";
import BarChart from "../components/BarChart";

export const CustomerFeedbackStatistics: FunctionComponent = () => {
  const classes = useStyles();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("xs"));
  const dispatch = useDispatch();
  const { t } = useI18n();

  let date = new Date();
  const [dateRange, setDateRange] = useState([
    new Date(date.getFullYear(), date.getMonth(), 1),
    moment().toDate()
  ]);

  const [showGraph, setShowGraph] = useState(false);
  const [userId, setUserId] = useState("");

  const statisticsState = useSelector<
    AppState,
    CustomerFeedbacksStatisticsState
  >(s => s.customerFeedbackStatistics);

  let { customerFeedbackStatistics, isLoading } = statisticsState;

  useEffect(() => {
    if (dateRange)
      dispatch(
        thunkFetchCustomerFeedbackStatistics(dateRange[0], dateRange[1])
      );
  }, [dateRange]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowGraph(event.target.checked);
  };

  const populateFeedbackDetails = (rowData: any) => {
    return customerFeedbackStatistics.reduce((acc: any, curr) => {
      if (rowData.storeName === curr.storeName) {
        acc.push({
          created: moment(curr.createdDate).format("YYYY-MM-DD"),
          stars: curr.starRating,
          comment: curr.comment,
          userId: curr.userId
        });
      }
      return acc;
    }, []);
  };

  const populateLatestFeedback = () => {
    return customerFeedbackStatistics.reduce((acc: any, curr) => {
      if (
        moment(curr.createdDate).format("YYYY-MM-DD") ===
        moment().format("YYYY-MM-DD")
      ) {
        acc.push({
          created: moment(curr.createdDate).format("YYYY-MM-DD HH:mm:ss"),
          store: curr.storeName,
          stars: curr.starRating,
          comment: curr.comment,
          userId: curr.userId
        });
      }
      return acc;
    }, []);
  };

  const populateChartDataPerDay = (): LineChartData => {
    const feedbackPerDay = customerFeedbackStatistics
      .reduce((acc: any, curr) => {
        let index = acc.findIndex(
          (a: { createdDate: string }) =>
            a.createdDate === moment(curr.createdDate).format("YYYY-MM-DD")
        );
        if (index < 0) {
          acc.push({
            createdDate: moment(curr.createdDate).format("YYYY-MM-DD"),
            rating5: curr.starRating === 5 ? curr.starCount : 0,
            rating4: curr.starRating === 4 ? curr.starCount : 0,
            rating3: curr.starRating === 3 ? curr.starCount : 0,
            rating2: curr.starRating === 2 ? curr.starCount : 0,
            rating1: curr.starRating <= 1 ? curr.starCount : 0,
            total: curr.starCount
          });
        } else {
          acc[index].rating1 += curr.starRating <= 1 ? curr.starCount : 0;
          acc[index].rating2 += curr.starRating === 2 ? curr.starCount : 0;
          acc[index].rating3 += curr.starRating === 3 ? curr.starCount : 0;
          acc[index].rating4 += curr.starRating === 4 ? curr.starCount : 0;
          acc[index].rating5 += curr.starRating === 5 ? curr.starCount : 0;
          acc[index].total += curr.starCount;
        }
        return acc;
      }, [])
      .sort((a: any, b: any) => (a.createdDate > b.createdDate ? 1 : -1));

    return {
      labels: feedbackPerDay.map((f: any) => f.createdDate),
      datasets: [
        {
          label: "5 stars",
          rgbColor: { r: 31, g: 132, b: 52 },
          data: feedbackPerDay.map((f: any) => f.rating5)
        },
        {
          label: "4 stars",
          rgbColor: { r: 5, g: 23, b: 141 },
          data: feedbackPerDay.map((f: any) => f.rating4)
        },
        {
          label: "3 stars",
          rgbColor: { r: 5, g: 89, b: 141 },
          data: feedbackPerDay.map((f: any) => f.rating3)
        },
        {
          label: "2 stars",
          rgbColor: { r: 240, g: 155, b: 13 },
          data: feedbackPerDay.map((f: any) => f.rating2)
        },
        {
          label: "1 star",
          rgbColor: { r: 195, g: 88, b: 66 },
          data: feedbackPerDay.map((f: any) => f.rating1)
        },
        {
          label: "Total",
          rgbColor: { r: 205, g: 200, b: 196 },
          data: feedbackPerDay.map((f: any) => f.total)
        }
      ]
    };
  };

  const populateFeedbackTable = () => {
    const storeRating = customerFeedbackStatistics.reduce((acc: any, curr) => {
      if (!acc[curr.storeName])
        acc[curr.storeName] = {
          average: 0,
          rating5: 0,
          rating4: 0,
          rating3: 0,
          rating2: 0,
          rating1: 0
        };

      acc[curr.storeName].rating1 += curr.starRating <= 1 ? curr.starCount : 0;
      acc[curr.storeName].rating2 += curr.starRating === 2 ? curr.starCount : 0;
      acc[curr.storeName].rating3 += curr.starRating === 3 ? curr.starCount : 0;
      acc[curr.storeName].rating4 += curr.starRating === 4 ? curr.starCount : 0;
      acc[curr.storeName].rating5 += curr.starRating === 5 ? curr.starCount : 0;
      return acc;
    }, {});

    return Object.keys(storeRating).reduce((acc: any, key) => {
      let value = storeRating[key];
      acc.push({
        storeName: key,
        average:
          (value.rating5 * 5 +
            value.rating4 * 4 +
            value.rating3 * 3 +
            value.rating2 * 2 +
            value.rating1 * 1) /
          (value.rating5 +
            value.rating4 +
            value.rating3 +
            value.rating2 +
            value.rating1),
        rating5: value.rating5,
        rating4: value.rating4,
        rating3: value.rating3,
        rating2: value.rating2,
        rating1: value.rating1
      });
      return acc;
    }, []);
  };
  const feedback = populateFeedbackTable();

  return (
    <>
      <DateRangePicker
        disabled={isLoading}
        onChange={setDateRange}
        value={dateRange}
      />
      <Grid
        container
        direction={"row"}
        justifyContent={"space-evenly"}
        alignItems={"flex-start"}
        style={{ marginTop: "2rem" }}
        spacing={mobile ? 0 : 2}
      >
        <Grid item xs={12} lg={8}>
          <TableWrapper title={t("common.statistics.customerRating")}>
            <Hidden smDown>
              <Grid
                style={{
                  position: "absolute",
                  zIndex: 100,
                  width: "160px",
                  margin: "25px 0 0 10px"
                }}
                component="label"
                container
                justifyContent="flex-start"
                alignItems="center"
                spacing={1}
              >
                <Grid item>{t("common.messages.table")}</Grid>
                <Grid item>
                  <Switch
                    size={"small"}
                    checked={showGraph}
                    onChange={handleChange}
                    name="graphSwitch"
                    disabled={isLoading}
                  />
                </Grid>
                <Grid item>{t("common.messages.chart")}</Grid>
              </Grid>
            </Hidden>
            {showGraph ? (
              <BarChart
                chartData={{
                  labels: feedback.map((p: any) => p.storeName),
                  datasets: [
                    {
                      label: "5 stars",
                      rgbColor: { r: 31, g: 132, b: 52 },
                      data: feedback.map((p: any) => p.rating5)
                    },
                    {
                      label: "4 stars",
                      rgbColor: { r: 5, g: 23, b: 141 },
                      data: feedback.map((p: any) => p.rating4)
                    },
                    {
                      label: "3 stars",
                      rgbColor: { r: 5, g: 89, b: 141 },
                      data: feedback.map((p: any) => p.rating3)
                    },
                    {
                      label: "2 stars",
                      rgbColor: { r: 240, g: 155, b: 13 },
                      data: feedback.map((p: any) => p.rating2)
                    },
                    {
                      label: "1 star",
                      rgbColor: { r: 195, g: 88, b: 66 },
                      data: feedback.map((p: any) => p.rating1)
                    }
                  ]
                }}
                showLegend={true}
                isLoading={isLoading}
              />
            ) : (
              <StatTable
                exportName={"Customer feedback statistics"}
                columns={[
                  {
                    title: "Store",
                    field: "storeName"
                  },
                  {
                    title: "Average",
                    field: "average",
                    render: (rowData: any) => (
                      <Tooltip
                        title={rowData.average.toFixed(2)}
                        aria-label={"rating"}
                        arrow
                      >
                        <span>
                          <Rating
                            name="average"
                            defaultValue={rowData.average}
                            readOnly
                            precision={0.25}
                            size={"small"}
                            aria-label={"rating"}
                          />
                        </span>
                      </Tooltip>
                    ),
                    customSort: (a: any, b: any) => {
                      return a.average - b.average;
                    }
                  },
                  {
                    title: "5 stars",
                    field: "rating5",
                    hidden: mobile
                  },
                  {
                    title: "4 stars",
                    field: "rating4",
                    hidden: mobile
                  },
                  {
                    title: "3 stars",
                    field: "rating3",
                    hidden: mobile
                  },
                  {
                    title: "2 stars",
                    field: "rating2",
                    hidden: mobile
                  },
                  {
                    title: "1 star",
                    field: "rating1",
                    hidden: mobile
                  }
                ]}
                data={!isLoading ? feedback : []}
                detailPanel={(rowData: any) => {
                  return (
                    <CustomerFeedbackDetailsTable
                      feedbacks={populateFeedbackDetails(rowData)}
                    />
                  );
                }}
                isLoading={isLoading}
              />
            )}
          </TableWrapper>
        </Grid>
        <Grid item xs={12} lg={4}>
          <TableWrapper title={"Today's ratings"}>
            <StatTable
              dense
              columns={[
                {
                  title: "Created",
                  field: "created",
                  cellStyle: { padding: "8px" }
                },
                {
                  title: "Store",
                  field: "store",
                  hidden: mobile,
                  cellStyle: { padding: "8px" }
                },
                {
                  title: "Stars",
                  field: "stars",
                  cellStyle: { padding: "8px" },
                  render: (rowData: any) => (
                    <Tooltip title={rowData.comment} arrow>
                      <span>
                        <Rating
                          name="average"
                          defaultValue={rowData.stars === 0 ? 1 : rowData.stars}
                          readOnly
                          precision={0.25}
                          size={"small"}
                          aria-label={"rating"}
                        />
                      </span>
                    </Tooltip>
                  )
                },
                {
                  title: "",
                  field: "comment",
                  cellStyle: { padding: "8px" },
                  hidden: mobile,
                  render: (rowData: any) => (
                    <Tooltip title={rowData.comment} arrow>
                      <Message
                        style={{
                          fill: rowData.comment ? "#05598d" : "#e9e5e2"
                        }}
                      />
                    </Tooltip>
                  )
                }
              ]}
              data={populateLatestFeedback().sort((a: any, b: any) =>
                a.created < b.created ? 1 : -1
              )}
              actions={[
                {
                  icon: "account_circle",
                  iconProps: { style: { color: "#1f8434" } },
                  tooltip: "Go to User",
                  onClick: (event: any, rowData: any) =>
                    setUserId(rowData.userId)
                }
              ]}
              pageSize={5}
              pageSizeOptions={[5, 10, 20]}
              isLoading={isLoading}
            />
          </TableWrapper>
        </Grid>
        <Grid item xs={12}>
          <LineChart
            title={t("common.statistics.customerFeedback")}
            pointRadius={4}
            tension={0.01}
            border={1}
            isLoading={isLoading}
            chartData={populateChartDataPerDay()}
          />
        </Grid>
      </Grid>

      <Dialog
        onClose={() => setUserId("")}
        open={userId !== ""}
        fullWidth={true}
        fullScreen={mobile}
        maxWidth={"xl"}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle style={{ height: 50 }} disableTypography>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={() => setUserId("")}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent style={mobile ? { padding: 0 } : {}}>
          <UserOverview inputUserId={userId} />
        </DialogContent>
      </Dialog>
    </>
  );
};
